import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Tooltip from '@groovehq/internal-design-system/lib/components/Tooltip/Tooltip'
import ExternalLink from '@groovehq/internal-design-system/lib/assets/icons/ExternalLink'
import config from 'config'
import loadChooser from 'util/dropbox/loadChooser'
import { openDropboxChooser, updateComposerIdentity } from './dropboxChooser'

export function useDropboxLinkAction({
  getEditor,
  contextKey,
  draftId,
  onChange,
}) {
  const [sdk, setSdk] = useState(null)
  const [failed, setFailed] = useState(false)
  const lifecycle = useRef({
    contextKey,
    draftId,
    generation: 0,
    mounted: true,
  })
  const live = lifecycle.current
  const latest = useRef({ getEditor, onChange })
  const selection = useRef(null)
  const configured = !!config.dropboxAppKey?.trim()
  const captureSelection = () => {
    const editor = latest.current.getEditor?.()
    let bookmark = null
    try {
      bookmark = editor.selection.getBookmark(2, true)
    } catch (e) {
      // Missing selections fall back to the end of the originating editor.
    }
    selection.current = { editor, bookmark, generation: live.generation }
  }

  useLayoutEffect(
    () => {
      // Only committed identity changes may invalidate an open popup. A new,
      // unsaved composer can acquire its first draft ID while it is open.
      updateComposerIdentity(live, contextKey, draftId)
      latest.current = { getEditor, onChange }
    },
    [live, contextKey, draftId, getEditor, onChange]
  )

  useLayoutEffect(
    () => {
      live.mounted = true
      return () => {
        live.mounted = false
        live.generation += 1
      }
    },
    [live]
  )

  useEffect(
    () => {
      if (!configured) return undefined
      let disposed = false
      loadChooser(config.dropboxAppKey).then(
        chooser => {
          if (!disposed) setSdk(chooser)
        },
        () => {
          if (!disposed) setFailed(true)
        }
      )
      return () => {
        disposed = true
      }
    },
    [live, configured]
  )

  const notify = message => app.doShowSnackbar(message)
  const onClick = () => {
    if (!sdk) return
    if (!selection.current) captureSelection()
    const captured = selection.current
    selection.current = null
    const { editor, bookmark } = captured
    if (
      !editor ||
      captured.generation !== live.generation ||
      editor !== latest.current.getEditor?.()
    ) {
      notify('Dropbox links were not inserted because the composer changed.')
      return
    }
    // A second invocation supersedes any still-open chooser callback.
    live.generation += 1
    const generation = live.generation
    try {
      // Do not await here: Dropbox must open synchronously in the user gesture.
      openDropboxChooser({
        sdk,
        editor,
        bookmark,
        isCurrent: () =>
          live.mounted &&
          live.generation === generation &&
          latest.current.getEditor() === editor,
        onChange: (...args) => latest.current.onChange(...args),
        notify,
        onStale: () => {
          if (live.mounted) {
            notify(
              'Dropbox links were not inserted because the composer changed.'
            )
          }
        },
      })
    } catch (e) {
      notify('Could not open Dropbox. Please try again.')
    }
  }
  let title = 'Loading Dropbox…'
  if (sdk) title = 'Insert from Dropbox'
  if (failed) {
    title = 'Dropbox is unavailable. Please try again later.'
  }

  return { configured, ready: !!sdk, failed, title, onClick, captureSelection }
}

export default function DropboxLinkButton(props) {
  const { ready, title, onClick } = useDropboxLinkAction(props)
  return (
    <Tooltip title={title} position="top">
      <span>
        <Button
          type="icon"
          size="small"
          aria-label="Insert from Dropbox"
          disabled={!ready}
          onClick={onClick}
          data-test-id="insert-dropbox-links"
        >
          <ExternalLink />
        </Button>
      </span>
    </Tooltip>
  )
}
